import { useState, useEffect } from "react";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { useStepperContext } from "~/components/ui/stepper";
import FileSelectShadcn from "./FileSelectShadcn";
import { FileUploadArea } from "./FileUploadArea";
import {
  StepHeader,
  ValidationJobCard,
  SummaryItem,
  StatCard,
  StatusBadge,
} from "./SharedComponents";
import { useIngestionContext } from "./context/IngestionContext";
import { ValidationJob, ColumnProfileDisplay } from "./types";

/** Convert file key or S3 URI to frontend-friendly display path */
const formatFileForDisplay = (fileKey: string | null | undefined): string | null => {
  if (!fileKey) {
    return null;
  }

  // Strip s3://bucket-name/ prefix if present
  let bareKey = fileKey;
  if (fileKey.startsWith("s3://")) {
    const withoutProtocol = fileKey.slice(5);
    const slashIndex = withoutProtocol.indexOf("/");
    if (slashIndex !== -1) {
      bareKey = withoutProtocol.slice(slashIndex + 1);
    }
  }

  return resolveRouterPath(bareKey);
};

/** Step 1: File selection via upload or cloud storage */
export const FileUploadStep = () => {
  const {
    selectedFile,
    selectFile,
    pendingLocalFile,
    setPendingLocalFile,
    isUploaded,
    error,
    hasIngestion,
  } = useIngestionContext();
  const stepperContext = useStepperContext();

  const ingestion = stepperContext?.stepperState?.ingestion;
  const recoveredSourceFile = stepperContext?.stepperState?.recoveredSourceFile as string | null;
  const isLoadingIngestion = stepperContext?.stepperState?.isLoadingIngestion as
    | boolean
    | undefined;

  // Local state for FileSelectShadcn - syncs to context on selection
  const [localSelectedFile, setLocalSelectedFile] = useState<FileInfo | null>(selectedFile);

  // Source file from context, ingestion record, or parsed job results
  const sourceFileKey = selectedFile?.key || ingestion?.source_file || recoveredSourceFile;
  const isLoadingFileInfo = hasIngestion && isLoadingIngestion && !sourceFileKey;
  const isStartingFresh = !!pendingLocalFile && hasIngestion;

  // Handle cloud file selection - clears pending upload (mutually exclusive)
  const handleLocalFileChange = (file: FileInfo) => {
    selectFile(file);
    if (pendingLocalFile) {
      setPendingLocalFile(null);
    }
  };

  // Sync local state from context or recovered source file on mount/resume
  useEffect(() => {
    if (selectedFile && selectedFile.key !== localSelectedFile?.key) {
      setLocalSelectedFile(selectedFile);
    } else if (!localSelectedFile && sourceFileKey && !selectedFile) {
      const recoveredFileInfo: FileInfo = {
        key: sourceFileKey.startsWith("s3://")
          ? sourceFileKey.slice(sourceFileKey.indexOf("/", 5) + 1)
          : sourceFileKey,
        type: FileType.FILE,
        size: 0,
      };
      setLocalSelectedFile(recoveredFileInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, selectedFile?.key, sourceFileKey, localSelectedFile?.key]);

  return (
    <div className="space-y-4">
      <StepHeader
        title="Source File"
        subtitle="Upload a new file or select an existing file from cloud storage to validate. Supported formats: CSV and TSV"
      />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* File Upload Area - Upload from device */}
      <FileUploadArea
        onFileSelected={setPendingLocalFile}
        selectedFile={pendingLocalFile}
        onClearFile={() => setPendingLocalFile(null)}
        isUploadComplete={isUploaded}
      />

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or</span>
        </div>
      </div>

      {/* Cloud Storage Section */}
      <div>
        <h4 className="text-base font-medium mb-3">Select from Cloud Storage</h4>
        <FileSelectShadcn
          selectedFile={isStartingFresh ? null : localSelectedFile}
          setSelectedFile={setLocalSelectedFile}
          onSelectFile={handleLocalFileChange}
          placeholder={isStartingFresh ? "Using uploaded file instead..." : "Select source file..."}
          isLoading={isLoadingFileInfo}
          disabled={isStartingFresh}
        />
      </div>
    </div>
  );
};

/** Step 2: Display validation job results and status */
export const FileValidationStep = () => {
  const { selectedFile, isUploading, pendingLocalFile } = useIngestionContext();
  const { stepperState } = useStepperContext();

  if (!stepperState) {
    console.error("FileValidationStep: stepperState is undefined");
    return <div>Initializing...</div>;
  }

  const { validationState, isLoadingIngestion, ingestion } = stepperState;
  const recoveredSourceFile = stepperState?.recoveredSourceFile as string | null;

  // File display priority: current selection > pending upload name > ingestion record > job results
  const sourceFileKey = selectedFile?.key || ingestion?.source_file || recoveredSourceFile;
  const pendingFileName = pendingLocalFile?.name;
  const displayFileName = sourceFileKey
    ? formatFileForDisplay(sourceFileKey)
    : pendingFileName || null;

  const getOverallStatus = (): "passed" | "warning" | "running" | "failed" | "uploading" => {
    if (isUploading) {
      return "uploading";
    }
    if (!validationState?.isComplete) {
      return "running";
    }
    if (validationState.hasErrors) {
      return "failed";
    }
    if (validationState.hasWarnings) {
      return "warning";
    }
    return "passed";
  };

  if (isLoadingIngestion) {
    return (
      <div className="space-y-6">
        <StepHeader title="File Validation" />
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">Loading ingestion data...</p>
        </div>
      </div>
    );
  }

  // Show uploading state when file is being uploaded
  if (isUploading) {
    return (
      <div className="space-y-6">
        <div>
          <StepHeader title="File Validation" />
          {displayFileName && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Source file:</span>
              <span className="font-medium">{displayFileName}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-gray-600">Status:</span>
            <StatusBadge status="uploading" />
          </div>
        </div>

        <div className="p-8 text-center">
          <p className="text-gray-600">Uploading file to cloud storage...</p>
          <p className="text-sm text-gray-500 mt-2">
            Validation will begin automatically once upload completes.
          </p>
        </div>
      </div>
    );
  }

  // Show loading if no validation state yet (upload complete, waiting for validation to start)
  if (!validationState) {
    return (
      <div className="space-y-6">
        <div>
          <StepHeader title="File Validation" />
          {displayFileName && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Source file:</span>
              <span className="font-medium">{displayFileName}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-gray-600">Status:</span>
            <StatusBadge status="running" />
          </div>
        </div>
        <div className="p-8 text-center">
          <p className="text-gray-600">Starting validation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <StepHeader title="File Validation" />
        {displayFileName && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Source file:</span>
            <span className="font-medium">{displayFileName}</span>
          </div>
        )}
        {/* Top-level status badge */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-sm text-gray-600">Status:</span>
          <StatusBadge status={getOverallStatus()} />
        </div>
      </div>

      {/* Validation Jobs */}
      <div className="space-y-4">
        {validationState.jobs.map((job: ValidationJob, index: number) => (
          <ValidationJobCard key={index} {...job} />
        ))}
      </div>

      {/* Review Validation Summary */}
      {validationState.isComplete && (
        <div
          className={
            validationState.hasErrors ? "bg-red-50 rounded-lg p-4" : "bg-gray-50 rounded-lg p-4"
          }
        >
          <h4 className="font-medium mb-3">Review Validation Summary</h4>
          <div className="space-y-2 text-sm">
            <SummaryItem label="Total Rows in File" value={validationState.summary.totalRows} />
            <SummaryItem
              label="Header Row"
              value={validationState.summary.headerRow ? "yes" : "no"}
            />
            <SummaryItem label="Data Rows" value={validationState.summary.dataRows} />
            <SummaryItem label="Data Rows with Errors" value={validationState.summary.errorRows} />
            <SummaryItem label="Clean Data Rows" value={validationState.summary.cleanRows} />
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            {validationState.hasErrors ? (
              <>
                <p className="text-sm text-red-700 font-medium">Validation failed</p>
                <p className="text-sm text-red-600">
                  Please review the validation errors above and correct your file before proceeding.
                </p>
              </>
            ) : validationState.hasWarnings ? (
              <>
                <p className="text-sm text-gray-700 font-medium">
                  Validation completed with warnings
                </p>
                <p className="text-sm text-gray-600">
                  Review the warnings above. You may proceed, but some data may require attention.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 font-medium">
                  All {validationState.summary.cleanRows} data rows validated successfully!
                </p>
                <p className="text-sm text-gray-600">
                  No errors detected. Ready to proceed with data profiling.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SummaryStep
 *
 * Uses IngestionContext for selected file info.
 * Uses StepperContext for data profile.
 */
export const SummaryStep = () => {
  const { selectedFile } = useIngestionContext();
  const stepperContext = useStepperContext();

  const { stepperState } = stepperContext;
  const dataProfile = stepperState?.dataProfile;
  const profilingError = stepperState?.profilingError;
  const isProfiling = stepperState?.isProfiling;
  const ingestion = stepperState?.ingestion;
  const recoveredSourceFile = stepperState?.recoveredSourceFile as string | null;

  // Determine the source file to display:
  // 1. selectedFile from context (user just selected it in this session)
  // 2. ingestion.source_file from backend (resumed ingestion)
  // 3. recoveredSourceFile extracted from job results
  const sourceFileKey = selectedFile?.key || ingestion?.source_file || recoveredSourceFile;
  const displayFileName = sourceFileKey ? formatFileForDisplay(sourceFileKey) : null;

  // Show loading state
  if (isProfiling) {
    return (
      <div className="space-y-6">
        <StepHeader title="Column Summary" />
        <div className="p-8 text-center">
          <p className="text-gray-600">Analyzing data profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if profiling failed
  if (profilingError) {
    return (
      <div className="space-y-6">
        <StepHeader title="Column Summary" />
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Profiling Failed</h3>
              <p className="mt-1 text-sm text-red-700">{profilingError.message}</p>
              {profilingError.retryable && (
                <p className="mt-2 text-sm text-red-600">
                  Please go back and try uploading your file again.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no profile
  if (!dataProfile) {
    return (
      <div className="space-y-6">
        <StepHeader title="Column Summary" />
        <div className="p-8 text-center">
          <p className="text-gray-600">No data profile available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <StepHeader title="Column Summary" />
        {displayFileName && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Source file:</span>
            <span className="font-medium">{displayFileName}</span>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Columns" value={dataProfile.statistics.totalColumns} color="gray" />
        <StatCard
          label="Categorical Columns"
          value={dataProfile.statistics.categoricalColumns}
          color="blue"
        />
        <StatCard label="Good Rows" value={dataProfile.statistics.goodRows} color="green" />
        <StatCard label="Error Rows" value={dataProfile.statistics.errorRows} color="red" />
      </div>

      {/* Column Summary Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3">
          <div className="grid grid-cols-4 gap-4 text-sm font-medium text-gray-700">
            <span>Column Name</span>
            <span>Data Type</span>
            <span>Categorical</span>
            <span>Null %</span>
          </div>
        </div>

        <div className="divide-y">
          {dataProfile.columns.map((column: ColumnProfileDisplay, index: number) => (
            <div key={index} className="grid grid-cols-4 gap-4 p-4 text-sm hover:bg-gray-50">
              <span className="font-medium">{column.name}</span>
              <span>{column.type}</span>
              <span>{column.categorical ? "yes" : "no"}</span>
              <span>{column.nullPercent}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="text-sm text-gray-600">
        This profile analyzes your data structure, column types, and data quality. Review the
        findings above. They will inform the database schema and table structure for ingestion.
      </div>
    </div>
  );
};
